<template>
  <div class="audiences row">
    <simple-modal
      v-if="audienceDeleteModal"
      class="template-delete-modal"
      @rejected="audienceDeleteModal = null"
    >
      <div class="text-2xl text-center">
        are you sure you want to delete<br />this audience?
      </div>
      <template slot="buttons">
        <div class="flex flex-col">
          <button class="sb-button" @click="deleteAudience">
            delete audience
          </button>
          <button
            class="text-blue text-xl mt-5 cursor-pointer"
            @click="audienceDeleteModal = null"
          >
            cancel
          </button>
        </div>
      </template>
    </simple-modal>

    <div class="flex justify-between items-start">
      <div class="flex flex-col">
        <div>
          <h1 class="page-header inline mr-3">view audience</h1>
          <div class="text-lg text-blue inline-block">
            <select-dropdown
              :values="
                [7, 30, 60, 90, 365].map((n) => ({
                  label: `last ${n} days`,
                  value: n,
                }))
              "
              :selected.sync="timeRange"
              :disabled="!isAudienceBuilt"
              @update:selected="fetchAudienceKpis"
              variant="inline"
            >
              <template #label>
                <div class="flex text-2xl">last {{ timeRange }} days</div>
              </template>
            </select-dropdown>
          </div>
        </div>
      </div>
      <div class="text-lg text-blue flex">
        <h1 class="inline font-primary" style="font-size:2rem">&nbsp;</h1>
        <select-dropdown
          :values="[
            { label: 'refresh audience', value: 'refresh', disabled: !audienceDefault.ready_to_refresh },
            { label: 'edit audience', value: 'edit' },
            { label: 'delete audience', value: 'delete' },
          ]"
          @update:selected="executeAudienceActions"
          variant="inline"
          class="inline-block"
        >
          <template #label>
            <div class="flex text-2xl">actions</div>
          </template>
        </select-dropdown>
      </div>
    </div>
    <div>
      <div class="text-xl leading-none">
        <span class="mr-2">analyze the activity of your audience</span>
        <span class="text-base text-gray block md:inline-block">
          <span v-if="!!isAudienceBuilt">
            last updated {{ formatTime(audienceDefault.last_updated_at) }}
          </span>

          <span v-if="!!audienceDefault.next_refresh_time" class="ml-2">(scheduled at {{ formatTime(audienceDefault.next_refresh_time) }})</span>
          <span v-else-if="audienceDefault.state === 'queued'" class="ml-2">(queued)</span>
          <span v-else-if="audienceDefault.state === 'building'" class="ml-2">(building)</span>
        </span>
      </div>
      <div
        class="text-sm text-error"
        v-if="hasError"
      >
        an error has occured. please refresh the page or contact support if
        the issue remains
      </div>
    </div>

    <div class="flex flex-col items-center md:flex-row md:items-start mt-6 gap-10">

      <audiencePreview :membersCount="totalMemberCount"
                       :name="audienceDefault.name"
                       :isKpiDataLoading="!isAudienceBuilt || isLoadingGeneric"
                       :percentProgress="audienceGeneric && audienceGeneric.member_count_change"
      >
        <div v-if="!isAudienceBuilt" class="mt-8 mx-5 text-center">
          data will be available once the audience is finished building
        </div>
        <template v-else v-for="item in audienceGenericItems">
          <div class="w-full py-4" v-if="item.divider" :key="item.key">
            <div
              class="
                w-full
                border-t border-blue-light border-solid border-b-0
              "
            ></div>
          </div>
          <div class="flex justify-between w-full text-xl py-1 h-9">
            <span>{{ item.label }}</span>
            <span v-if="!isLoadingGeneric && audienceGeneric">
              {{ formatNumber(audienceGeneric[item.key], item.symbol) }}
            </span>
            <loading-icon v-else class="small text-xs"  />
          </div>
        </template>
      </audiencePreview>

      <div class="flex flex-1 flex-col gap-10">
        <div
          class="flex justify-between flex-col items-center md:flex-row gap-10 md:gap-0 w-full"
        >
          <AudienceKpiTile name="visits" :audienceKpi="audienceVisits" :isLoading="!isAudienceBuilt || isLoadingVisits">
            <template v-slot:rightTitle>{{ shortenNumber(audienceVisits.total) }}</template>
            <template v-slot:rightSubtitle>{{ audienceVisits.average }} per day</template>
          </AudienceKpiTile>
          <AudienceKpiTile name="members" :audienceKpi="audienceMembers" :isLoading="!isAudienceBuilt || isLoadingMembers">
            <template v-slot:rightTitle>{{ audienceMembers.total }} <span class="text-md">added</span></template>
            <template v-slot:rightSubtitle>{{ audienceMembers.removed_total }} removed</template>
          </AudienceKpiTile>
        </div>

        <div
          class="flex justify-between flex-col items-center md:flex-row gap-10 md:gap-0 w-full"
        >
          <AudienceKpiTile name="spend" :audienceKpi="audienceSpend" :isLoading="!isAudienceBuilt || isLoadingSpend">
            <template v-slot:rightTitle>${{ shortenNumber(audienceSpend.total) }}</template>
            <template v-slot:rightSubtitle>${{ audienceSpend.average }} avg ticket</template>
          </AudienceKpiTile>
          <AudienceKpiTile name="redemptions" :audienceKpi="audienceRedemptions" :isLoading="!isAudienceBuilt || isLoadingRedemptions">
            <template v-slot:rightTitle>{{ audienceRedemptions.total }}</template>
            <template v-slot:rightSubtitle>{{ audienceRedemptions.points_total }} {{ audienceRedemptions.points_total | pluralize('point') }}</template>
          </AudienceKpiTile>
        </div>

        <div
          class="
            top-selling
            flex
            border-1 border-solid border-blue rounded-2xl
            px-3 py-2
          "
        >
          <div
            v-if="!isAudienceBuilt || isLoadingTopSelling"
            class="flex justify-center items-center w-full"
          >
            <loading-icon class="opacity-50" />
          </div>
          <div
            class="flex flex-col w-full"
            v-else
          >
            <div class="flex justify-between w-full text-xl">
              <div class="flex">
                <div>top selling</div>
                <div class="text-blue pl-2">
                  <select-dropdown
                    :values="['brand', 'category']"
                    :selected.sync="topSellingTarget"
                    @update:selected="fetchAudienceTopSelling"
                    variant="inline"
                  />
                </div>
              </div>
              <div class="text-blue">
                <select-dropdown
                  :values="[
                    { label: 'spend', value: 'total_spend' },
                    { label: 'visit', value: 'total_visits' },
                    { label: 'unit', value: 'units_sold' },
                  ]"
                  :selected.sync="topSellingOrder"
                  :renderLabel="val => 'by ' + val"
                  @update:selected="fetchAudienceTopSelling"
                  variant="inline"
                />
              </div>
            </div>
            <div class="flex flex-col mt-5">
              <div class="flex">
                <span class="flex w-10"></span>
                <span class="flex flex-1 text-left text-gray">item</span>
                <span class="flex w-32 justify-center text-gray"
                  >units sold</span
                >
                <span class="flex w-32 justify-center text-gray"
                  >total spend</span
                >
                <span class="flex w-32 justify-center text-gray"
                  >total visits</span
                >
              </div>
              <div
                class="flex py-2"
                v-for="(item, key) in audienceTopSelling"
                :key="key"
              >
                <popper
                  trigger="hover"
                  :options="{ placement: 'right-start' }"
                  :visible-arrow="true"
                  :delay-on-mouse-over="0"
                >
                  <div class="popper w-60 shadow-none border border-solid border-blue rounded-md">
                    <div class="p-2 text-md"><span class="font-bold">{{ item.name }}</span> is ranked <span class="font-bold">{{ `#${item.rank}` }}</span> amongst your audience and was {{ item.previous_rank ? "ranked" : "" }} <span class="font-bold">{{ item.previous_rank !== null ? `#${item.previous_rank}` : "not ranked" }}</span> the preceding period</div>
                    <div class="text-right text-xs font-bold mr-1" :class="{
                      'text-gray-400': item.trend === 0 && item.previous_rank !== null,
                      'text-red-400': item.trend < 0 && item.previous_rank !== null,
                      'text-green-400': item.trend > 0 || item.previous_rank === null,
                    }">
                      trending {{ item.trend === 0 ? "the same" : ((item.trend > 0 || item.previous_rank === null) ? "up" : "down") }}
                      <TrendArrow :value="item.previous_rank === null ? 1 : item.trend" />
                    </div>
                  </div>
                  <div
                    slot="reference"
                    class="flex w-10"
                    :class="{
                      'text-gray-400': item.trend === 0 && item.previous_rank !== null,
                      'text-red-400': item.trend < 0 && item.previous_rank !== null,
                      'text-green-400': item.trend > 0 || item.previous_rank === null,
                    }"
                  >
                    <div class="flex justify-between w-6">
                      {{ item.rank }}
                      <TrendArrow :value="item.previous_rank === null ? 1 : item.trend" />
                    </div>
                  </div>
                </popper>
                <span class="flex flex-1 text-left">{{ item.name }}</span>
                <span class="flex w-32 justify-center">{{
                  shortenNumber(item.units_sold)
                }}</span>
                <span class="flex w-32 justify-center"
                  >${{ shortenNumber(item.total_spend) }}</span
                >
                <span class="flex w-32 justify-center">{{
                  shortenNumber(item.total_visits)
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex gap-10 justify-center mt-16">
      <button class="sb-button" @click="onNewCampaignClick">send audience campaign</button>
      <button class="sb-button" @click="onNewOfferClick">send audience member offer</button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { LoadingIcon, SelectDropdown, SimpleModal } from "@springbig/sb-components";
import { audiencesAPI } from "../../services/api/audiencesAPI";
import { campaignAPI } from "../../services/api/campaignAPI";
import { memberOffersAPI } from "../../services/api/memberOffersAPI";
import { commafyNumber, shortenNumber } from "../../utilities";
import TrendArrow from "./TrendArrow";
import AudiencePreview from "./AudiencePreview";
import AudienceKpiTile from "./AudienceKpiTile";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import Popper from 'vue-popperjs';

dayjs.extend(utc);
dayjs.extend(tz);

export default {
  name: "AudienceShow",
  components: {
    AudiencePreview,
    AudienceKpiTile,
    TrendArrow,
    SelectDropdown,
    LoadingIcon,
    SimpleModal,
    Popper,
  },
  data() {
    return {
      isLoadingDefault: false,
      audienceDefault: {},
      isLoadingGeneric: false,
      audienceGeneric: null,
      audienceGenericItems: [
        { label: "allowed sms", key: "allowed_sms_count", symbol: "", divider: true },
        { label: "allowed email", key: "allowed_email_count", symbol: "", divider: false },
        { label: "allowed push", key: "allowed_push_count", symbol: "", divider: false },
        { label: "allowed loyalty", key: "allowed_loyalty_count", symbol: "", divider: false },
        { label: "text messages sent", key: "sms_sent", symbol: "", divider: true },
        { label: "emails sent", key: "emails_sent", symbol: "", divider: false },
        { label: "push sent", key: "push_sent", symbol: "", divider: false },
        { label: "marketing cost", key: "marketing_cost", symbol: "$", divider: false },
        { label: "total spend", key: "total_revenue", symbol: "$", divider: true },
        { label: "ROI", key: "roi", symbol: "%", divider: false },
        { label: "referrals generated", key: "referrals_generated", symbol: "", divider: true },
        { label: "referral revenue", key: "referral_revenue", symbol: "$", divider: false },
      ],
      isLoadingVisits: false,
      audienceVisits: null,
      isLoadingMembers: false,
      audienceMembers: null,
      isLoadingSpend: false,
      audienceSpend: null,
      isLoadingRedemptions: false,
      audienceRedemptions: null,
      isLoadingTopSelling: false,
      audienceTopSelling: null,
      isLoadingKpis: false,
      timeRange: 7,
      topSellingTarget: "brand",
      topSellingOrder: "total_spend",
      error: {
        default: null,
        generic: null,
        visits: null,
        members: null,
        spend: null,
        redemptions: null,
        topSelling: null,
        other: null,
      },
      audienceDeleteModal: null,
      pollTimer: null,
    };
  },
  async created() {
    await this.$store.dispatch("LOAD_CURRENT_MERCHANT");
    await this.fetchAudience();
    // Fetch kpis only if the first build is done
    if(this.audienceDefault.last_updated_at) {
      this.fetchAudienceKpis();
    }
  },
  destroyed() {
    if(this.pollTimer) window.clearTimeout(this.pollTimer);
  },
  computed: {
    totalMemberCount() {
      return this.audienceGeneric?.total_member_count ?? 0
    },
    audienceId() {
      return this.$route.params.id;
    },
    isAudienceBuilt() {
      return this.audienceDefault.last_updated_at != null;
    },
    hasError() {
      return this.error.default ||
        this.error.generic ||
        this.error.visits ||
        this.error.members ||
        this.error.spend ||
        this.error.redemptions ||
        this.error.topSelling ||
        this.error.other;
    },
    ...mapGetters(["merchantTimeZone"]),
  },
  methods: {
    onNewCampaignClick() {
      campaignAPI.createCampaign({
        campaign: {
          merchant_segment_ids:[this.audienceDefault.merchant_segment.id]
        }
      }).then( ({campaign}) => {
        if(!campaign?.id) {
          this.error.other = true;
        }else{
          document.location.href = `/campaigns/${campaign.id}/edit`
        }
      }).catch( (error) => {
        this.error.other = error.message;
      })
    },
    onNewOfferClick() {
      memberOffersAPI.createMemberOffer({
        member_offer: {
          merchant_segment_ids:[this.audienceDefault.merchant_segment.id]
        }
      }).then( ({member_offer}) => {
        if(!member_offer?.id) {
          this.error.other = true;
        }else{
          document.location.href = `/member_offers/${member_offer.id}/edit`
        }
      }).catch( (error) => {
        this.error.other = error.message;
      })
    },
    pollAudience() {
      this.pollTimer = window.setTimeout(async () => {
        await this.fetchAudience();
        if(["completed", "failed"].includes( this.audienceDefault.state )) {
          this.pollTimer = null;
          if(this.audienceDefault.state === "completed") this.fetchAudienceKpis();
        }else{
          this.pollAudience();
        }
      },30 * 1000)
    },
    formatTime(time) {
      return dayjs
        .utc(time)
        .tz(this.merchantTimeZone)
        .format('MM/DD/YYYY, h:mma')
    },
    shortenNumber(num) {
      return shortenNumber(num);
    },
    formatNumber(num, symbol) {
      return num ? (symbol === "$" ? "$" : "") + commafyNumber(num) + (symbol === "%" ? "%" : "") : 0;
    },
    executeAudienceActions(action) {
      switch (action) {
        case "refresh":
          this.refreshAudience();
          break;
        case "edit":
          this.$router.push(`/audiences/${this.audienceDefault.id}/edit`)
          break;
        case "delete":
          this.audienceDeleteModal = true;
          break;
      }
    },
    fetchAudienceKpis() {
      if( this.isLoadingKpis ) return;
      const requestTimeRange = this.timeRange;
      const params = {
        period: `last_${this.timeRange}`,
      };
      this.isLoadingKpis = true;
      Promise.all([
        this.fetchAudienceKpi(this.audienceId, "generic", params),
        this.fetchAudienceKpi(this.audienceId, "visits", params),
        this.fetchAudienceKpi(this.audienceId, "members", params),
        this.fetchAudienceKpi(this.audienceId, "spend", params),
        this.fetchAudienceKpi(this.audienceId, "redemptions", params),
        this.fetchAudienceTopSelling()
      ]).finally(() => {
        this.isLoadingKpis = false;
        if( this.timeRange !== requestTimeRange ) {
          this.fetchAudienceKpis()
        }
      })
    },
    async fetchAudienceTopSelling() {
      if (this.audienceId) {
        const params = {
          period: `last_${this.timeRange}`,
          attr_key: this.topSellingTarget,
          sort_key: this.topSellingOrder,
        };
        await this.fetchAudienceKpi(this.audienceId, "top_selling", params);
      }
    },
    async fetchAudience() {
      this.isLoadingDefault = true;
      try {
        this.audienceDefault = await audiencesAPI.fetchAudience(this.audienceId);
        this.error.default = null;
      } catch (error) {
        this.error.default = "error, please refresh or contact support";
      } finally {
        this.isLoadingDefault = false;
      }
    },
    async fetchAudienceKpi(audienceId, kpiName = "", params = {}) {
      switch (kpiName) {
        case "generic":
          this.isLoadingGeneric = true;
          try {
            const { results } = await audiencesAPI.fetchAudienceKpi(
              audienceId,
              "generic",
              params
            );
            this.audienceGeneric = results;
            this.error.generic = null;
          } catch (error) {
            this.error.generic = "error, please refresh or contact support";
          } finally {
            this.isLoadingGeneric = false;
          }
          break;
        case "visits":
          this.isLoadingVisits = true;
          try {
            const { results } = await audiencesAPI.fetchAudienceKpi(
              audienceId,
              "visits",
              params
            );
            this.audienceVisits = results;
            this.error.visits = null;
          } catch (error) {
            this.error.visits = "error, please refresh or contact support";
          } finally {
            this.isLoadingVisits = false;
          }
          break;
        case "members":
          this.isLoadingMembers = true;
          try {
            const { results } = await audiencesAPI.fetchAudienceKpi(
              audienceId,
              "members",
              params
            );
            this.audienceMembers = results;
            this.error.members = null;
          } catch (error) {
            this.error.members = "error, please refresh or contact support";
          } finally {
            this.isLoadingMembers = false;
          }
          break;
        case "spend":
          this.isLoadingSpend = true;
          try {
            const { results } = await audiencesAPI.fetchAudienceKpi(
              audienceId,
              "spend",
              params
            );
            this.audienceSpend = results;
            this.error.spend = null;
          } catch (error) {
            this.error.spend = "error, please refresh or contact support";
          } finally {
            this.isLoadingSpend = false;
          }
          break;
        case "redemptions":
          this.isLoadingRedemptions = true;
          try {
            const { results } = await audiencesAPI.fetchAudienceKpi(
              audienceId,
              "redemptions",
              params
            );
            this.audienceRedemptions = results;
            this.error.redemptions = null;
          } catch (error) {
            this.error.redemptions = "error, please refresh or contact support";
          } finally {
            this.isLoadingRedemptions = false;
          }
          break;
        case "top_selling":
          this.isLoadingTopSelling = true;
          try {
            const { results } = await audiencesAPI.fetchAudienceKpi(
              audienceId,
              "top_selling",
              params
            );
            this.audienceTopSelling = results;
            this.error.topSelling = null;
          } catch (error) {
            this.error.topSelling = "error, please refresh or contact support";
          } finally {
            this.isLoadingTopSelling = false;
          }
          break;
      }
    },
    async deleteAudience() {
      if (this.audienceId) {
        try {
          await audiencesAPI.deleteAudience(this.audienceId);
          this.$router.push("/audiences")
        } catch (error) {
          this.error.default = "error, please refresh or contact support";
        }
      }
    },
    async refreshAudience() {
      if (this.audienceId) {
        try {
          await audiencesAPI.refreshAudience(this.audienceId);
          this.fetchAudience();
          this.fetchAudienceKpis()
        } catch (error) {
          this.error.default = "error, please refresh or contact support";
        }
      }
    },
  },

  watch: {
    'audienceDefault.state': {
      immediate: true,
      handler(state) {
        // If the audience is building, waits for the built to be done to update the kpis.
        if(['queued', 'in_progress'].includes(state) && this.pollTimer == null) {
          this.pollAudience();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.audiences {
  @media only screen and (min-width: 768px) {
    min-width: 65rem;
    max-width: 67rem;
  }
  .template-delete-modal {
    &::v-deep {
      .content {
        min-width: 420px;
      }
    }
  }
  .sb-loading-icon.small {
    width: 25px;
  }
  .sb-dropdown::v-deep {
    .sb-rows-container {
      width: 200px;
      text-align: center;
      left:calc(50% - 100px);
    }
  }
  .top-selling {
    min-height: 340px;
    .sb-dropdown::v-deep {
      .sb-rows-container {
        width: auto;
        border: none;
        padding: 0;
        margin: 0;
        background-color: transparent;
        right: -5px;
        left: auto;
        .body-rows {
          display: flex;
          font-size: 18px;
          line-height: 15px;
          >main {
            padding: 0 5px;
            &:not(:first-child) {
              border-left:1px solid #0009;
            }
          }
        }
      }
    }
    div.popper::v-deep {
      margin-top: -5px;
      margin-left: -5px;
      .popper__arrow {
        border-left: 1px solid #128ff9;
        border-top: 1px solid transparent;
        border-right: 1px solid transparent;
        border-bottom: 1px solid #128ff9;
        width: 10px;
        height: 10px;
        transform: rotate(45deg);
        background: white;
        margin-top: 7px;
      }
    }
  }
}
</style>
